import * as React from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';
import Icon from '../../foundations/Icons';

export type Props = {
  children: React.ReactNode;
  showBorder?: boolean;
  tight?: boolean;
  name?: string;
  inheritOverflow?: boolean;
};

/**
 * A futuro table
 *
 * @argument first - accepted by the <Table.HeadColumn>. used by tables with checkboxes as a first column and applies relevant styles for it.
 * @argument last - accepted by the <Table.HeadColumn> and <Table.Column>. used by last columns, applies a sticky position and relevant styles for it.
 * @argument sortable - accepted by the <Table.HeadColumn>. used for sortable headers. table headers become a button that will be used for sorting.
 * @argument bold - accepted by the <Table.Column>. can be used to apply a bold font to a table cell
 *
 * Based off the designs in the figma file:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=3886%3A13150
 *
 * @example
 *
 * <Table.Table>
 *   <Table.Head>
 *     <Table.HeadRow>
 *       <Table.HeadColumn first>Name</Table.HeadColumn>
 *       <Table.HeadColumn sortable>Email</Table.HeadColumn>
 *       <Table.HeadColumn last>Action</Table.HeadColumn>
 *     </Table.HeadRow>
 *   </Table.Head>
 *   <Table.Body>
 *     <Table.Row>
 *       <Table.Column bold>Jane Doe</Table.Column>
 *       <Table.Column>janedoe@mail.com</Table.Column>
 *       <Table.Column><Button /></Table.Column>
 *     </Table.Row>
 *   </Table.Body>
 * </Table.Table>
 */

export const Table = ({
  children,
  name,
  showBorder = true,
  tight = false,
  inheritOverflow = false,
}: Props) => {
  const wrapperClasses = classNames(
    'block',
    'box-border',
    'border-[0.063rem] border-solid border-foreground-subtle',
    'rounded-[0.313rem]',
    tight ? 'm-0' : 'mx-0 mb-0 mt-xxl',
    'bg-regular',
    'w-full',
    !inheritOverflow && 'overflow-auto',
    showBorder
      ? 'max-xs:border-b-[0.063rem] max-xs:border-solid max-xs:border-foreground-subtle'
      : 'max-xs:border-none'
  );

  return (
    <div role="wrapper" className={wrapperClasses}>
      <table
        className="table w-full table-auto border-collapse overflow-auto border-none"
        aria-label={name}
      >
        {children}
      </table>
    </div>
  );
};

export default Table;

export const Body = (
  props: React.PropsWithChildren<{ className?: string }>
) => (
  <tbody className={classNames('w-full', props.className)}>
    {props.children}
  </tbody>
);

export const StyledLink = (
  props: React.PropsWithChildren<{
    to: string;
    href?: undefined;
    onClick?: undefined;
  }>
) => {
  const linkClasses = classNames(
    'flex',
    'w-full',
    'hover:text-primary-special',
    'hover:underline'
  );

  return (
    <Link {...props} className={linkClasses}>
      {props.children}
    </Link>
  );
};

export const StyledA = (
  props: React.PropsWithChildren<
    {
      href?: string;
      onClick?: () => void;
    } & React.AnchorHTMLAttributes<HTMLAnchorElement>
  >
) => {
  const anchorClasses = classNames(
    'flex',
    'w-full',
    'hover:text-primary-special',
    'hover:underline'
  );

  return (
    <a {...props} className={anchorClasses}>
      {props.children}
    </a>
  );
};

export type RowProps = {
  error?: boolean;
  active?: boolean;
  children: React.ReactNode;
  noBorder?: boolean;
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  onClick?: React.MouseEventHandler;
  draggable?: boolean;
  innerRef?: any;
  dragProps?: any;
  isDragging?: boolean;
  disabled?: boolean;
  /** Highlight row for 2 seconds */
  highlight?: boolean;
  /** Remove bottom border when row is expanded */
  isExpanded?: boolean;
  /** type will define the background color. Normally used together with isExpanded as errors and warnings are displayed in the expanded row */
  type?: 'error' | 'warning' | null;
};

export const Row = ({
  active,
  error,
  children,
  noBorder = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
  draggable,
  innerRef,
  dragProps,
  isDragging,
  disabled,
  highlight,
  isExpanded,
  type,
}: RowProps) => {
  if (disabled) {
    const disabledClasses = classNames(
      'table-row w-full cursor-not-allowed border-b border-foreground-subtle align-middle bg-subtle text-subtle'
    );
    return <tr className={disabledClasses}>{children}</tr>;
  }

  const rowClasses = classNames(
    'w-full',
    !noBorder &&
      'border-solid border-foreground-subtle [&:not(:last-child)]:border-b-[0.063rem]',
    isDragging ? 'table border-[0.125rem] border-dashed' : 'table-row',
    onClick ? 'cursor-pointer' : 'cursor-default',
    {
      'bg-special [&>td]:bg-special [&>td]:hover:bg-special [:hover_+_&>td]:bg-special':
        active,
      'bg-regular [&>td]:bg-regular [&>td]:hover:bg-special max-xs:bg-special':
        !active,
      '[&>td]:animate-highlight': highlight,
      '[&+tr>td]:hover:bg-special [&+tr>td]:pt-0': isExpanded,
    }
  );

  return (
    <tr
      data-testid="table-row"
      className={rowClasses}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      ref={innerRef}
      {...(draggable && dragProps.draggableProps)}
      {...(draggable && dragProps.dragHandleProps)}
    >
      {children}
    </tr>
  );
};

export type ColumnProps = Omit<
  React.TdHTMLAttributes<HTMLTableCellElement>,
  'align'
> & {
  bold?: boolean;
  first?: boolean;
  last?: boolean;
  sticky?: boolean;
  size?: number;
  width?: string;
  /** Set column alignment. Currency should be right aligned. Defaults to left */
  align?: React.TdHTMLAttributes<HTMLTableCellElement>['align'];
  /** If enabled, the collumn/cell will take the width of the content. Default to false, meaning it can grow more than content */
  fitContent?: boolean;
  // @deprecated to be removed
  className?: string;
};
/**
 * This works on a 12 column grid
 */
export const Column = (props: React.PropsWithChildren<ColumnProps>) => {
  const {
    last,
    size,
    first,
    sticky,
    children,
    bold,
    width,
    className,
    fitContent,
    align,
    ...rest
  } = props;

  let columnClasses = classNames(
    className,
    !width && !fitContent && 'w-auto',
    !last && 'px-s py-[1.125rem]',
    last && 'px-s',
    'font-body',
    bold ? 'text-label-small' : 'text-body-small',
    'text-regular'
  );

  if (sticky) {
    columnClasses = classNames(
      columnClasses,
      'p-s',
      `grow-[${size}]`,
      'sticky',
      'z-10',
      first && 'left-0',
      last && 'right-0'
    );
  }

  return (
    <td
      width={fitContent ? '1%' : width}
      align={align}
      className={columnClasses}
      {...rest}
    >
      {children}
    </td>
  );
};

export type LinkValue =
  | { to: string; href?: undefined; onClick?: undefined }
  | { to?: undefined; href: string; onClick?: undefined }
  | { to?: undefined; href?: undefined; onClick: () => void };

type LinkProps = LinkValue & { size?: number; children: React.ReactNode };

export const LinkColumn = (props: LinkProps & ColumnProps) => {
  if (props.to) {
    return (
      <Column {...props}>
        <StyledLink to={props.to}>{props.children}</StyledLink>
      </Column>
    );
  }

  if (props.href) {
    return (
      <Column {...props}>
        <StyledA href={props.href}>{props.children}</StyledA>
      </Column>
    );
  }

  return (
    <Column {...props}>
      <StyledA onClick={props.onClick}>{props.children}</StyledA>
    </Column>
  );
};

const SortHeader = (
  props: React.PropsWithChildren<
    Pick<HeadColumnSortableProps, 'onClick' | 'active' | 'isIconVisible'>
  >
) => {
  const { onClick, active, isIconVisible, children } = props;
  const buttonClasses = classNames(
    'flex',
    'bg-transparent',
    'border-none',
    'rounded-xxxs',
    'hover:bg-subtle',
    '[&>div]:hover:visible'
  );

  const iconWrapperClasses = classNames(
    'pl-xxs',
    'pt-0.5',
    isIconVisible ? 'visible' : 'invisible',
    'text-subtle'
  );

  const titleClasses = classNames(
    'text-subtle',
    'text-label-small',
    'font-body'
  );

  return (
    <button className={buttonClasses} onClick={onClick}>
      <span className={titleClasses}>{children}</span>
      <div className={iconWrapperClasses}>
        <Icon
          name={active === 'down' ? 'sArrowDown' : 'sArrowUp'}
          inheritFill
        />
      </div>
    </button>
  );
};

export const Head = (props: React.PropsWithChildren<{ sticky?: boolean }>) => (
  <thead
    className={classNames(
      'top-0 z-10 w-full rounded-t-xxxs border-b-[0.063rem] border-solid border-foreground-subtle text-left align-middle bg-subtle',
      {
        sticky: !!props.sticky,
      }
    )}
  >
    {props.children}
  </thead>
);

type HeadColumnProps = {
  width?: HTMLTableCellElement['width'];
  last?: boolean;
  first?: boolean;
  sticky?: boolean;
  sortable?: false;
  /** Set column alignment. Currency should be right aligned. Defaults to left */
  align?: React.ThHTMLAttributes<HTMLTableCellElement>['align'];
  /** @deprecated The property should not be used */
  className?: string;
  strong?: boolean;
};

type HeadColumnSortableProps = Omit<HeadColumnProps, 'sortable'> & {
  sortable: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  active?: 'down' | 'up';
  isIconVisible?: boolean;
};

export const HeadColumn = (
  props: React.PropsWithChildren<HeadColumnProps | HeadColumnSortableProps>
) => {
  const {
    children,
    width,
    sortable,
    last,
    sticky,
    first,
    className,
    align,
    strong,
  } = props;

  let headerClasses = classNames(
    !width && 'w-auto',
    'px-s py-[1.125rem]',
    'text-label-small',
    'font-body',
    'bg-subtle',
    [strong ? 'text-strong' : 'text-subtle']
  );

  if (sticky) {
    headerClasses = classNames(
      headerClasses,
      'pl-auto',
      'sticky',
      'z-10',
      first && 'left-0',
      last && 'right-0'
    );
  }

  return (
    <th
      // @ts-expect-error Can't get the types for width correct...
      width={width}
      className={classNames(headerClasses, className)}
      align={align}
    >
      {sortable ? (
        <SortHeader
          onClick={props.onClick}
          active={props.active}
          isIconVisible={props.isIconVisible}
        >
          {children}
        </SortHeader>
      ) : (
        children
      )}
    </th>
  );
};

export const HeadRow = (
  props: React.PropsWithChildren<{ className?: string }>
) => {
  const headRowClasses = classNames('w-full', props.className);
  return <tr className={headRowClasses}>{props.children}</tr>;
};
